import { Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import { UserRole } from "@/types/enums";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { lazyPage } from "@/utils/lazyWithReload";

const LoginPage = lazyPage(() => import("@/pages/LoginPage").then(m => ({ default: m.LoginPage })));
const LandingPage = lazyPage(() => import("@/pages/LandingPage").then(m => ({ default: m.LandingPage })));
const DashboardPage = lazyPage(() => import("@/pages/DashboardPage").then(m => ({ default: m.DashboardPage })));
const CompaniesPage = lazyPage(() => import("@/pages/admin/CompaniesPage").then(m => ({ default: m.CompaniesPage })));
const SitesPage = lazyPage(() => import("@/pages/admin/SitesPage").then(m => ({ default: m.SitesPage })));
const UsersPage = lazyPage(() => import("@/pages/admin/UsersPage").then(m => ({ default: m.UsersPage })));
const DrillCreatePage = lazyPage(() => import("@/pages/DrillCreatePage").then(m => ({ default: m.DrillCreatePage })));
const StageConfigurePage = lazyPage(() => import("@/pages/StageConfigurePage").then(m => ({ default: m.StageConfigurePage })));
const DrillExecutePage = lazyPage(() => import("@/pages/DrillExecutePage").then(m => ({ default: m.DrillExecutePage })));
const DrillEditPage = lazyPage(() => import("@/pages/DrillEditPage").then(m => ({ default: m.DrillEditPage })));
const DrillReportTextEditPage = lazyPage(() => import("@/pages/DrillReportTextEditPage").then(m => ({ default: m.DrillReportTextEditPage })));
const DrillDetailPage = lazyPage(() => import("@/pages/DrillDetailPage").then(m => ({ default: m.DrillDetailPage })));
const ParticipantManagePage = lazyPage(() => import("@/pages/ParticipantManagePage").then(m => ({ default: m.ParticipantManagePage })));
const LegacyDocumentsPage = lazyPage(() => import("@/pages/LegacyDocumentsPage").then(m => ({ default: m.LegacyDocumentsPage })));
const ReviewTemplatesPage = lazyPage(() => import("@/pages/admin/ReviewTemplatesPage").then(m => ({ default: m.ReviewTemplatesPage })));
const DrillTypesPage = lazyPage(() => import("@/pages/admin/DrillTypesPage").then(m => ({ default: m.DrillTypesPage })));
const EvaluationPage = lazyPage(() => import("@/pages/EvaluationPage").then(m => ({ default: m.EvaluationPage })));
const ReportPreviewPage = lazyPage(() => import("@/pages/ReportPreviewPage").then(m => ({ default: m.ReportPreviewPage })));
const ArchivePage = lazyPage(() => import("@/pages/ArchivePage").then(m => ({ default: m.ArchivePage })));
const NotificationsPage = lazyPage(() => import("@/pages/NotificationsPage").then(m => ({ default: m.NotificationsPage })));
const AdminPanelPage = lazyPage(() => import("@/pages/admin/AdminPanelPage").then(m => ({ default: m.AdminPanelPage })));

const ADMIN_ROLES = [UserRole.SUPERADMIN, UserRole.DIRECTOR_TECH];
const DRILL_ROLES = [UserRole.TECH, UserRole.DIRECTOR_TECH, UserRole.SUPERADMIN];

function LoadingFallback() {
  return <div className="flex items-center justify-center h-64 text-gray-500">Loading…</div>;
}

export function AppRoutes() {
  // Keyed on the path so leaving a failed screen gives the next one a fresh start.
  const { pathname } = useLocation();
  return (
    <AppErrorBoundary key={pathname}>
    <Suspense fallback={<LoadingFallback />}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRoles={ADMIN_ROLES}>
            <Layout>
              <AdminPanelPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/companies"
        element={
          <ProtectedRoute requiredRoles={ADMIN_ROLES}>
            <Layout>
              <CompaniesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sites"
        element={
          <ProtectedRoute requiredRoles={ADMIN_ROLES}>
            <Layout>
              <SitesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredRoles={ADMIN_ROLES}>
            <Layout>
              <UsersPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/review-templates"
        element={
          <ProtectedRoute requiredRoles={ADMIN_ROLES}>
            <Layout>
              <ReviewTemplatesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/drill-types"
        element={
          <ProtectedRoute requiredRoles={ADMIN_ROLES}>
            <Layout>
              <DrillTypesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/drills/new"
        element={
          <ProtectedRoute requiredRoles={DRILL_ROLES}>
            <Layout>
              <DrillCreatePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/drills/:id/stages"
        element={
          <ProtectedRoute requiredRoles={DRILL_ROLES}>
            <Layout>
              <StageConfigurePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/drills/:id/execute"
        element={
          <ProtectedRoute requiredRoles={DRILL_ROLES}>
            <Layout>
              <DrillExecutePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/drills/:id/edit"
        element={
          <ProtectedRoute requiredRoles={DRILL_ROLES}>
            <Layout>
              <DrillEditPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/drills/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <DrillDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/drills/:id/report-text"
        element={
          <ProtectedRoute requiredRoles={DRILL_ROLES}>
            <Layout>
              <DrillReportTextEditPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/drills/:id/participants"
        element={
          <ProtectedRoute requiredRoles={DRILL_ROLES}>
            <Layout>
              <ParticipantManagePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/drills/:id/evaluation"
        element={
          <ProtectedRoute requiredRoles={DRILL_ROLES}>
            <Layout>
              <EvaluationPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/drills/:id/preview"
        element={
          <ProtectedRoute requiredRoles={[...DRILL_ROLES, UserRole.CLIENT]}>
            <Layout>
              <ReportPreviewPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/legacy-documents"
        element={
          <ProtectedRoute>
            <Layout>
              <LegacyDocumentsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/archive"
        element={
          <ProtectedRoute>
            <Layout>
              <ArchivePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Layout>
              <NotificationsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </AppErrorBoundary>
  );
}
